import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import SendSnapshotDialog from './SendSnapshotDialog';

const BUCKET = 'skyway-dev-373d5.appspot.com';

const TERMINAL = ['signed', 'cancelled', 'expired', 'declined'];

const CHANNEL_ICONS = {
  'email': EmailOutlinedIcon,
  'signature-remote': DrawOutlinedIcon,
  'signature-in-person': TabletMacOutlinedIcon,
};

const CHANNEL_LABELS = {
  'email': 'Emailed',
  'signature-remote': 'Sent for signature',
  'signature-in-person': 'Signed in person',
};

// One chip per send state. Colors follow the app's rule: .dark for foreground on
// the light background, and never amber for a normal waiting state.
const statusChip = (send) => {
  if (!send.requiresSignature) return { label: 'Email only — no signature', color: 'default', variant: 'outlined' };
  switch (send.signatureStatus) {
    case 'signed': return { label: 'Signed', color: 'success', variant: 'filled' };
    case 'partially-signed': return { label: 'Partially signed', color: 'info', variant: 'outlined' };
    case 'cancelled': return { label: 'Cancelled', color: 'default', variant: 'outlined' };
    case 'expired': return { label: 'Expired', color: 'default', variant: 'outlined' };
    case 'declined': return { label: 'Declined', color: 'error', variant: 'outlined' };
    default: return { label: 'Awaiting signature', color: 'info', variant: 'outlined' };
  }
};

// A server timestamp is unresolved for a moment after the write, so fall back to
// the display string the server wrote alongside it.
const sentLabel = (send) => {
  if (send.sentAt && send.sentAt.seconds) return moment.unix(send.sentAt.seconds).format('MM/DD/YYYY h:mm A');
  return send.sentAtDisplay || '—';
};

const signedLabel = (send) => {
  if (send.signedAt && send.signedAt.seconds) return moment.unix(send.signedAt.seconds).format('MM/DD/YYYY h:mm A');
  return null;
};

const recipientText = (send) => (send.recipients || [])
  .map(r => (r.name ? `${r.name} <${r.email}>` : r.email))
  .join(', ');

// Every estimate and invoice that has gone out to the customer, newest first —
// what was on it, who it went to, and whether they signed.
//
// Read-only by design: these records are written server-side at send time so the
// history cannot drift from what was actually sent. Sends come from the modal's
// own listener (order.documentSends), so this component never queries.
export default function SendsTab({ order }) {
  const sends = order.documentSends || [];
  const loaded = !!order.documentSends_loaded;

  const [snapshotSend, setSnapshotSend] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const viewPdf = async (send) => {
    const pdf = send.pdf || {};
    // A signed copy beats the as-sent copy. The email path already holds a
    // long-lived signed URL, so prefer it and skip the round trip.
    const path = pdf.signedPath || pdf.path;
    try {
      if (!pdf.signedPath && pdf.url) return window.open(pdf.url, '_blank');
      if (!path) return StateManager.setAlertAndOpen('This send has no stored PDF.', 'warning');
      const url = await firebase.app().storage(BUCKET).ref(path).getDownloadURL();
      window.open(url, '_blank');
    } catch (e) {
      StateManager.setAlertAndOpen('Could not open the document.', 'error');
    }
  };

  const cancelRequest = async (send) => {
    setBusyId(send.id);
    try {
      const res = await RequestManager.post({
        function: 'cancelSignatureSession',
        variables: { document_uuid: send.sessionId || send.id },
      });
      if (res && res.status === 'complete') StateManager.setAlertAndOpen('Signature request cancelled.', 'success');
      else StateManager.setAlertAndOpen('Could not cancel the request.', 'error');
    } catch (e) {
      StateManager.setAlertAndOpen('Could not cancel the request.', 'error');
    }
    setBusyId(null);
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
    );
  }

  if (!sends.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>Nothing has been sent to the customer yet.</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Build the document on the Estimate tab, then email it or request a signature. Every send is recorded here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {sends.map((send) => {
        const chip = statusChip(send);
        const ChannelIcon = CHANNEL_ICONS[send.channel] || EmailOutlinedIcon;
        const active = send.requiresSignature && !TERMINAL.includes(send.signatureStatus);
        const signedOn = signedLabel(send);
        const lineCount = send.snapshot
          ? (send.snapshot.services || []).length + (send.snapshot.parts || []).length + (send.snapshot.subcontracts || []).length
          : 0;

        return (
          <Box
            key={send.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 2,
              mb: 2,
              bgcolor: (t) => (send.signatureStatus === 'signed' ? alpha(t.palette.success.main, 0.05) : 'transparent'),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <ChannelIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {(send.documentType === 'custom' && send.documentName) || send.documentLabel || 'Document'}
              </Typography>
              <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {CHANNEL_LABELS[send.channel] || 'Sent'} {sentLabel(send)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5 }}>
              {recipientText(send) && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>To: {recipientText(send)}</Typography>
              )}
              {!!(send.cc || []).length && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>cc: {send.cc.join(', ')}</Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sent by {send.sentByName || send.sentByEmail || '—'}
              </Typography>
              {signedOn && (
                <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                  Signed {signedOn}
                  {(send.signedBy || []).length ? ` by ${send.signedBy.map(s => s.name || s.email).join(', ')}` : ''}
                </Typography>
              )}
              {send.statusAdvance && (
                <Typography variant="body2" sx={{ color: 'info.dark' }}>
                  Advanced order: {send.statusAdvance.from} → {send.statusAdvance.to}
                </Typography>
              )}
              {send.approvalWriteBack && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Marked approved: {send.approvalWriteBack.services} services, {send.approvalWriteBack.parts} parts
                  {send.approvalWriteBack.subcontracts ? `, ${send.approvalWriteBack.subcontracts} subcontracted` : ''}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => viewPdf(send)}>
                {send.pdf && send.pdf.signedPath ? 'View signed document' : 'View PDF'}
              </Button>

              <Tooltip title={send.snapshot ? `${lineCount} line${lineCount === 1 ? '' : 's'} were on this document` : 'No line snapshot for this send'}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ListAltIcon />}
                    disabled={!send.snapshot}
                    onClick={() => setSnapshotSend(send)}
                  >
                    What was on it
                  </Button>
                </span>
              </Tooltip>

              {active && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={busyId === send.id ? <CircularProgress size={14} /> : <CancelIcon />}
                  disabled={busyId === send.id}
                  onClick={() => cancelRequest(send)}
                >
                  Cancel request
                </Button>
              )}
            </Box>
          </Box>
        );
      })}

      <SendSnapshotDialog open={!!snapshotSend} onClose={() => setSnapshotSend(null)} send={snapshotSend} />
    </Box>
  );
}
